import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Grid from "@mui/material/Grid";
import { Alert, Button } from "@mui/material";
import { PollItem, ChannelHeader, TextFieldCollectionCard } from "@Components";
import { IChannel, ITextFieldCollection, RootStateType } from "@Interfaces";
import { withPublicLayout } from "@Hocs";
import { DialogComponents } from "@Interfaces/Entities/Dialogs";
import { actions as dialogActions } from "@Store/Reducers/dialog";
import { getPollTextFields } from "@Services/API/PollTextField";
import { isGuestDataStored } from "@Utils/isGuestDataStored";
import { getChannelCoverUrl } from "@Utils/getChannelCoverUrl";
import { filterActiveChannels } from "@Utils/channelFilters";
import { useTranslation } from "react-i18next";
import { useChannelCompletionProgress } from "./useChannelCompletionProgress";
import { ChannelActions } from "@Store";
import { readDoubleVoteMeta } from "@Utils/doubleVoteMeta";
import { DOUBLE_VOTE_CLEAR_MS } from "./doubleVoteConstants";
import { buildPollsTabItems } from "../../Utils/componentOrderUtils";
import { isDataCollectionRequired } from "@Utils/isDataCollectionRequired";
import { getRedirectDomain } from "@Utils/redirectUrl";
import {
    readSubmittedTextFieldIds,
    rememberSubmittedTextField,
} from "@Utils/submittedTextFields";
import { useCompletionRedirect } from "./useCompletionRedirect";
import { hasVotedOnPoll } from "./channelCompletion.utils";

const ShareChannelPageComponent: React.FC = () => {
    const dispatch = useDispatch<any>();
    const { t } = useTranslation();
    const selectedChannel: IChannel = useSelector(
        (state: RootStateType) => state.channels.channels[0] || ({} as IChannel)
    );
    const channels = useSelector(
        (state: RootStateType) => state.channels.channels
    );
    const parentChannel = useSelector(
        (state: RootStateType) =>
            (state.channels.invitation as IChannel | undefined) || undefined
    );
    const activeChannelId = useSelector(
        (state: RootStateType) => state.channels.current
    );
    const activeChannel: IChannel | null =
        channels.find((channel) => channel._id === activeChannelId) ||
        channels[0] ||
        null;
    const [hasCompletedVoting, setHasCompletedVoting] = useState(false);
    const completionClearTimerRef = useRef<ReturnType<
        typeof setTimeout
    > | null>(null);
    // Tracks the last completion "cycle" that triggered the banner (1 = first pass, 2 = second pass).
    const lastShownCycleRef = useRef<number>(0);
    const lobbyShownRef = useRef(false);
    const dataCollectionPromptedChannelRef = useRef<string | null>(null);
    const [textFields, setTextFields] = useState<ITextFieldCollection[]>([]);
    // Channel whose text fields finished loading; until it matches, completion can't be judged.
    const [textFieldsLoadedFor, setTextFieldsLoadedFor] = useState<
        string | null
    >(null);
    // Seeded from storage so a returning participant who answered everything counts as finished.
    const [submittedTextFieldIds, setSubmittedTextFieldIds] = useState<
        string[]
    >(readSubmittedTextFieldIds);
    const [orderedItems, setOrderedItems] = useState<any[]>([]);
    const selectedChannelId = selectedChannel?._id;
    const componentOrderKey = (activeChannel?.componentOrder ?? [])
        .map((entry) => `${entry.type}:${entry.id}`)
        .join(",");

    useEffect(() => {
        lobbyShownRef.current = false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parentChannel?._id]);

    useEffect(() => {
        if (!channels.length) return;
        if (!activeChannelId && channels[0]?._id) {
            dispatch(ChannelActions.setActiveChannel(channels[0]._id));
        }
    }, [channels, activeChannelId, dispatch]);

    useEffect(() => {
        const activeChannelId = activeChannel?._id ?? null;
        if (!activeChannelId) {
            return;
        }

        const isParentLobby =
            parentChannel?.has_children &&
            (parentChannel.sub_channels?.length || 0) > 0 &&
            activeChannel?._id === parentChannel._id;

        const shouldOpenDataCollection =
            activeChannel?.polls?.length &&
            !isGuestDataStored() &&
            !isParentLobby &&
            isDataCollectionRequired(activeChannel.settings);

        // Prevent re-opening the data collection modal on every channel refresh
        // (for example after the delayed vote submit resolves).
        if (!shouldOpenDataCollection) {
            return;
        }

        if (dataCollectionPromptedChannelRef.current === activeChannelId) {
            return;
        }

        dataCollectionPromptedChannelRef.current = activeChannelId;

        const coverUrl = getChannelCoverUrl(activeChannel);
        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.DataCollectionDialog,
                props: {
                    settings: activeChannel.settings,
                    author: {
                        username: activeChannel.title,
                        avatar: activeChannel.profile_image,
                    },
                    coverUrl,
                },
            })
        );
    }, [activeChannel, parentChannel, dispatch]);

    const allowDoubleVote = Boolean(activeChannel?.settings?.allow_double_vote);

    useEffect(() => {
        // Finished means every poll AND every text field, matching Present Mode's count.
        // Double-vote events can't use Present Mode: they keep the poll-only completion.
        const requireTextFields = !allowDoubleVote;
        const textFieldsBelongToChannel =
            Boolean(activeChannel) &&
            textFieldsLoadedFor === activeChannel?._id;
        const waitingForTextFields =
            requireTextFields &&
            activeChannel?._id === selectedChannelId &&
            textFieldsLoadedFor !== selectedChannelId;
        const channelTextFields =
            requireTextFields && textFieldsBelongToChannel ? textFields : [];

        if (
            !activeChannel ||
            waitingForTextFields ||
            (!activeChannel.polls?.length && channelTextFields.length === 0)
        ) {
            setHasCompletedVoting(false);
            return;
        }

        const answeredTextFields = new Set(submittedTextFieldIds);
        const allTextFieldsAnswered = channelTextFields.every((textField) =>
            answeredTextFields.has(textField._id)
        );

        // Reset completion cycle when channel changes.
        lastShownCycleRef.current = 0;

        const channelPolls = activeChannel.polls ?? [];
        const channelPollIds = channelPolls.map((poll) => poll._id);
        const votedPolls = new Set(
            channelPolls.filter(hasVotedOnPoll).map((poll) => poll._id)
        );

        const cooldownMs = allowDoubleVote ? DOUBLE_VOTE_CLEAR_MS : 0;
        // Track min vote count across polls and latest double-vote timestamp.
        let minVoteCount = allowDoubleVote ? Number.POSITIVE_INFINITY : 1;
        const latestDoubleVoteAt =
            allowDoubleVote && cooldownMs >= 0
                ? channelPolls.reduce<number | null>((latest, poll) => {
                      if (!hasVotedOnPoll(poll)) return latest;

                      const meta = readDoubleVoteMeta(poll._id);
                      const ts = Number(meta?.lastVoteAt) || 0;
                      const count = Number(meta?.voteCount) || 0;
                      if (count < minVoteCount) {
                          minVoteCount = count;
                      }
                      if (!ts) return latest;
                      if (latest === null) return ts;
                      return ts > latest ? ts : latest;
                  }, null)
                : null;

        if (!allowDoubleVote) {
            minVoteCount = 1;
        } else if (!Number.isFinite(minVoteCount)) {
            // No meta found yet for voted polls.
            minVoteCount = 0;
        }

        const doubleVoteWindowSatisfied =
            !allowDoubleVote ||
            cooldownMs <= 0 ||
            latestDoubleVoteAt === null || // no meta recorded yet; allow first completion
            (latestDoubleVoteAt > 0 &&
                Date.now() - latestDoubleVoteAt < cooldownMs);

        const requiredCycle = allowDoubleVote
            ? Math.min(2, lastShownCycleRef.current + 1)
            : 1;
        const completedNow =
            votedPolls.size >= channelPollIds.length &&
            allTextFieldsAnswered &&
            doubleVoteWindowSatisfied &&
            minVoteCount >= requiredCycle;

        setHasCompletedVoting(completedNow);

        if (completionClearTimerRef.current) {
            clearTimeout(completionClearTimerRef.current);
            completionClearTimerRef.current = null;
        }

        if (allowDoubleVote && completedNow) {
            lastShownCycleRef.current = requiredCycle;
            completionClearTimerRef.current = setTimeout(() => {
                setHasCompletedVoting(false);
                completionClearTimerRef.current = null;
            }, cooldownMs);
        }

        if (!completedNow) {
            completionClearTimerRef.current = null;
        }
    }, [
        activeChannel,
        allowDoubleVote,
        selectedChannelId,
        textFields,
        textFieldsLoadedFor,
        submittedTextFieldIds,
    ]);

    // Prevent setState on unmounted component if navigation happens during the 60s window.
    useEffect(() => {
        return () => {
            if (completionClearTimerRef.current) {
                clearTimeout(completionClearTimerRef.current);
            }
        };
    }, []);

    useChannelCompletionProgress({
        channel: activeChannel,
        hasCompletedVoting,
        translate: t,
    });

    useEffect(() => {
        if (!parentChannel?._id) {
            return;
        }

        const hasChildren =
            parentChannel.has_children &&
            (parentChannel.sub_channels?.length || 0) > 0;

        if (!hasChildren) {
            return;
        }

        const activeSubChannels = filterActiveChannels(
            parentChannel.sub_channels ?? []
        );

        if (activeSubChannels.length === 0) {
            return;
        }

        const isParentActive =
            !activeChannel || activeChannel._id === parentChannel._id;

        if (!isParentActive) {
            return;
        }

        if (lobbyShownRef.current) {
            return;
        }

        // Single active sub-channel: auto-select it and skip the lobby dialog entirely.
        if (activeSubChannels.length === 1) {
            lobbyShownRef.current = true;
            const [targetChannel] = activeSubChannels;
            const targetLinkId = targetChannel.link_id ?? targetChannel._id;

            if (targetLinkId) {
                dispatch(
                    ChannelActions.getLinkChannelData(targetLinkId) as any
                );
                try {
                    const url = new URL(window.location.href);
                    url.searchParams.set("ch", targetLinkId);
                    window.history.replaceState({}, "", url.toString());
                } catch (error) {
                    // ignore history errors (older browsers)
                }
            }
            return;
        }

        lobbyShownRef.current = true;

        dispatch(
            dialogActions.openDialog({
                id: DialogComponents.SubChannelSelectionDialog,
                props: {
                    parentChannel,
                    subChannels: activeSubChannels,
                    fullScreen: true,
                    disableEscapeKeyDown: true,
                },
            })
        );
    }, [dispatch, parentChannel, activeChannel]);

    useEffect(() => {
        setTextFieldsLoadedFor(null);

        if (!selectedChannelId) {
            setTextFields([]);
            return;
        }

        let canceled = false;

        const fetchTextFields = async () => {
            try {
                const { data } = await getPollTextFields(selectedChannelId);
                if (!canceled) {
                    setTextFields(Array.isArray(data) ? data : []);
                    setTextFieldsLoadedFor(selectedChannelId);
                }
            } catch (error) {
                if (!canceled) {
                    // Left unmarked as loaded: no redirect beats skipping unseen questions.
                    setTextFields([]);
                }
            }
        };

        fetchTextFields();

        return () => {
            canceled = true;
        };
    }, [selectedChannelId]);

    useEffect(() => {
        if (!activeChannel) {
            setOrderedItems([]);
            return;
        }

        const polls = activeChannel.polls ?? [];
        const items = buildPollsTabItems(
            polls,
            textFields,
            activeChannel.componentOrder
        );
        setOrderedItems(items);
    }, [activeChannel, textFields, componentOrderKey]);

    const handleTextFieldSubmitted = useCallback((textFieldId: string) => {
        // Kept across visits so a returning participant who answered everything counts as finished.
        rememberSubmittedTextField(textFieldId);
        setSubmittedTextFieldIds((previous) =>
            previous.includes(textFieldId)
                ? previous
                : [...previous, textFieldId]
        );
    }, []);

    const { redirectUrl, showContinueLink } = useCompletionRedirect({
        channel: activeChannel,
        textFields:
            activeChannel?._id && textFieldsLoadedFor === activeChannel._id
                ? textFields
                : null,
        submittedTextFieldIds,
    });

    return (
        <>
            {activeChannel && (
                <>
                    <Grid item xs={12}>
                        <ChannelHeader {...activeChannel} />
                    </Grid>
                    {orderedItems.map((item) => (
                        <Grid
                            item
                            xs={12}
                            key={
                                item.type === "poll"
                                    ? item.poll._id
                                    : item.textField._id
                            }
                        >
                            {item.type === "poll" ? (
                                <PollItem
                                    {...item.poll}
                                    channel={activeChannel}
                                    votingTimeLimit={
                                        activeChannel.settings
                                            ?.voting_time_limit as number
                                    }
                                    isHideResult={
                                        activeChannel.settings
                                            ?.hide_results_before_voting as boolean
                                    }
                                />
                            ) : (
                                <TextFieldCollectionCard
                                    textField={item.textField}
                                    published={item.textField.createdAt}
                                    onSubmitted={handleTextFieldSubmitted}
                                />
                            )}
                        </Grid>
                    ))}
                    {showContinueLink && redirectUrl && (
                        <Grid item xs={12}>
                            <Alert
                                severity="info"
                                action={
                                    <Button
                                        color="inherit"
                                        size="small"
                                        href={redirectUrl}
                                        rel="noopener noreferrer"
                                    >
                                        {t(
                                            "invitation.completion_redirect_continue_button"
                                        )}
                                    </Button>
                                }
                            >
                                {t("invitation.completion_redirect_continue", {
                                    domain: getRedirectDomain(redirectUrl),
                                })}
                            </Alert>
                        </Grid>
                    )}
                </>
            )}
        </>
    );
};

export default withPublicLayout(ShareChannelPageComponent);
